// lib/phone-token.js - Phone token with triple storage (localStorage + IndexedDB + cookie)
// Primary anchor for trial abuse prevention

const LS_KEY = 'scantodesk_phone_id';
const COOKIE_NAME = 'stk_pid';
const IDB_DB_NAME = 'scantodesk';
const IDB_STORE_NAME = 'identity';
const IDB_KEY = 'phone_token';
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds

/**
 * Generate a UUID v4 with fallback for non-secure contexts
 */
function generateUUID() {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = crypto.getRandomValues(new Uint8Array(1))[0] & 15;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// --- Cookie helpers ---

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name, value) {
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Lax; Secure`;
}

// --- IndexedDB helpers ---

function openIDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(IDB_DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(IDB_STORE_NAME)) {
        db.createObjectStore(IDB_STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function getFromIDB() {
  try {
    const db = await openIDB();
    return new Promise((resolve) => {
      const tx = db.transaction(IDB_STORE_NAME, 'readonly');
      const store = tx.objectStore(IDB_STORE_NAME);
      const req = store.get(IDB_KEY);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function setToIDB(value) {
  try {
    const db = await openIDB();
    return new Promise((resolve) => {
      const tx = db.transaction(IDB_STORE_NAME, 'readwrite');
      const store = tx.objectStore(IDB_STORE_NAME);
      store.put(value, IDB_KEY);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    });
  } catch {
    return false;
  }
}

// --- Main API ---

/**
 * Get the phone token from triple storage (localStorage + IndexedDB + cookie).
 * If found in any source, syncs to all others.
 * If not found anywhere, generates a new one and stores in all three.
 * @returns {Promise<string>} The phone token (UUID v4)
 */
export async function getPhoneToken() {
  // Read from all three sources
  const fromLS = localStorage.getItem(LS_KEY);
  const fromCookie = getCookie(COOKIE_NAME);
  const fromIDB = await getFromIDB();

  // Use the first found value (prefer localStorage > IndexedDB > cookie)
  const token = fromLS || fromIDB || fromCookie || generateUUID();

  // Sync to all storage locations
  if (fromLS !== token) {
    try { localStorage.setItem(LS_KEY, token); } catch { /* ignore */ }
  }
  if (fromCookie !== token) {
    try { setCookie(COOKIE_NAME, token); } catch { /* ignore */ }
  }
  if (fromIDB !== token) {
    await setToIDB(token);
  }

  return token;
}
